/**
 * Componente para exibir grade de jobs/tasks ativos de uma demanda específica
 * Dashboard específico para o Gestor - Demandas sem projeto
 * 
 * @author Equipe GestaoeCRM
 * @date 08/10/2025
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectCardsApi } from '@/services/projectCardsApi';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { ArrowLeft, Clock, User, Play, AlertCircle, CheckCircle, ClipboardList } from 'lucide-react';

interface DemandActiveOSGridProps {
  demandId: string;
  demandName: string;
  onBack: () => void;
}

interface ActiveOSItem {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  estimatedHours: number | null;
  actualHours: number;
  createdAt: string;
  demand: {
    id: string;
    title: string;
    status: string;
    priority: string;
  };
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  workSessions?: Array<{
    id: string;
    startedAt: string;
    isPaused: boolean;
    pausedAt: string | null;
  }>;
}

export default function DemandActiveOSGrid({ demandId, demandName, onBack }: DemandActiveOSGridProps) {
  const { user } = useAuth();
  const [localActiveOS, setLocalActiveOS] = useState<ActiveOSItem[]>([]);

  // Buscar jobs/tasks ativos da demanda específica
  const { 
    data: activeOSData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['demandActiveOS', demandId],
    queryFn: () => projectCardsApi.getDemandActiveOS(demandId),
    enabled: !!demandId && user?.role === 'GESTOR',
    refetchInterval: 30000, // Atualizar dados do servidor a cada 30 segundos
  });

  // Atualizar dados locais quando dados do servidor mudarem
  useEffect(() => {
    if (activeOSData?.data) {
      setLocalActiveOS(activeOSData.data);
    }
  }, [activeOSData]);

  // Função para calcular duração de uma sessão ativa
  const calculateSessionDuration = (session: { startedAt: string; isPaused: boolean; pausedAt: string | null }) => {
    const now = new Date();
    const startTime = new Date(session.startedAt);
    const endTime = session.isPaused && session.pausedAt ? new Date(session.pausedAt) : now;
    return Math.round(((endTime.getTime() - startTime.getTime()) / (1000 * 60)) * 100) / 100; // em minutos
  };

  // Renderizar loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Renderizar erro
  if (error) {
    return (
      <div className="space-y-4">
        {/* Header com botão de voltar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar aos Cards</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <h2 className="text-xl font-semibold text-gray-900">
              Jobs/Tasks Ativos - {demandName}
            </h2>
          </div>
        </div>

        {/* Erro */}
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <AlertCircle className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium mb-2">Erro ao carregar jobs/tasks ativos</p>
          <p className="text-sm text-center mb-4">
            Não foi possível carregar os jobs/tasks ativos. Tente novamente.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const activeOS = localActiveOS;

  return (
    <div className="space-y-6">
      {/* Header com botão de voltar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar aos Cards</span>
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div className="flex items-center space-x-2">
            <ClipboardList className="h-5 w-5 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Jobs/Tasks Ativos - {demandName}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {activeOS.length} ordem{activeOS.length !== 1 ? 'ns' : ''} de serviço ativa{activeOS.length !== 1 ? 's' : ''} na demanda
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Jobs/Tasks Ativos */}
      {activeOS.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <CheckCircle className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium mb-2">Nenhum job/task ativo</p>
          <p className="text-sm text-center">
            Não há jobs/tasks ativos nesta demanda no momento.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Header da Grade */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-4">OS / Funcionário</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-2">Status / Sessão</div>
              <div className="col-span-2">Horas</div>
              <div className="col-span-2">Criado em</div>
            </div>
          </div>

          {/* Conteúdo da Grade */}
          <div className="bg-white divide-y divide-gray-200">
            {activeOS.map((os) => {
              const activeSession = os.workSessions && os.workSessions.length > 0 ? os.workSessions[0] : null;
              const sessionDuration = activeSession ? calculateSessionDuration(activeSession) : 0;
              
              return (
                <div key={os.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* OS / Funcionário */}
                    <div className="col-span-4">
                      <div className="flex items-center space-x-1">
                        <Play className="h-4 w-4 text-green-600" />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {os.title}
                          </h4>
                          <div className="flex items-center space-x-1 mt-1">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-600">
                              {os.user.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tipo */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-900">
                        {os.type}
                      </div>
                      {os.priority && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                          os.priority === 'CRITICA'
                            ? 'bg-red-100 text-red-800'
                            : os.priority === 'ALTA'
                            ? 'bg-orange-100 text-orange-800'
                            : os.priority === 'MEDIA'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {os.priority}
                        </span>
                      )}
                    </div>

                    {/* Status / Sessão */}
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {os.status}
                        </span>
                      </div>
                      {activeSession && (
                        <div className="text-xs text-gray-500 mt-1 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {sessionDuration.toFixed(0)}min
                        </div>
                      )}
                    </div>

                    {/* Horas */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-900">
                        {os.actualHours.toFixed(2)}h
                      </div>
                      {os.estimatedHours && (
                        <div className="text-xs text-gray-500">
                          / {os.estimatedHours}h
                        </div>
                      )}
                    </div>

                    {/* Criado em */}
                    <div className="col-span-2">
                      <div className="text-sm text-gray-900">
                        {new Date(os.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(os.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

